import { useCallback, useEffect } from 'react';
import { useStoreValue } from '@/lib/tinyStore';
/* Deep imports, not barrels: notifications ↔ calls would otherwise form an
   import cycle through their index files. */
import { isWithinQuietHours } from '@/features/notifications/quietHours';
import { useNotificationSettings } from '@/features/notifications/hooks/useNotificationSettings';
import { useSelfPresenceState } from '@/features/presence/hooks/usePresence';
import { callPopupActions, callPopupStore, selectCalls } from '../callPopupStore';
import { callsApi } from '../callsClient';
import type { IncomingCall } from '../types';

const SWEEP_MS = 1000;

export function useIncomingCalls(): readonly IncomingCall[] {
  return useStoreValue(callPopupStore, selectCalls);
}

/** Opens the ring subscription and replays anything already ringing. */
export function useCallRingFeed(): void {
  useEffect(() => {
    void callsApi
      .listRinging()
      .then((calls) => calls.forEach(callPopupActions.ring))
      .catch(() => undefined);

    return callsApi.subscribe({
      onRing: callPopupActions.ring,
      onCancel: callPopupActions.cancel,
    });
  }, []);
}

/** Expires calls whose server ring window closed. Runs only while ringing. */
export function useRingTimeout(hasCalls: boolean): void {
  useEffect(() => {
    if (!hasCalls) return undefined;
    const timer = window.setInterval(() => callPopupActions.sweep(), SWEEP_MS);
    return () => window.clearInterval(timer);
  }, [hasCalls]);
}

/**
 * Whether the ringtone may play: sound on, not inside quiet hours (unless the
 * user allows calls through), and not suppressed by Do not disturb unless the
 * caller is a priority caller.
 */
export function useShouldRing(call: IncomingCall | undefined): boolean {
  const settings = useNotificationSettings();
  const presence = useSelfPresenceState();

  if (!call) return false;
  if (!settings.soundEnabled) return false;

  const quiet = isWithinQuietHours(settings.quietHours);
  if (quiet && !(settings.quietHours.allowCalls || call.isPriorityCaller)) return false;

  const dnd = presence === 'doNotDisturb' || presence === 'presenting' || presence === 'screenSharing';
  return !(settings.respectDnd && dnd && !call.isPriorityCaller);
}

/** Accept / decline / reply, bound to one call. */
export function useCallActions(callId: string | undefined): {
  accept: (withVideo: boolean) => Promise<string | null>;
  decline: () => Promise<void>;
  reply: (message: string) => Promise<void>;
} {
  return {
    accept: useCallback(
      (withVideo: boolean) =>
        callId ? callPopupActions.accept(callId, withVideo) : Promise.resolve(null),
      [callId],
    ),
    decline: useCallback(
      () => (callId ? callPopupActions.decline(callId, 'declined') : Promise.resolve()),
      [callId],
    ),
    reply: useCallback(
      (message: string) =>
        callId ? callPopupActions.declineWithMessage(callId, message) : Promise.resolve(),
      [callId],
    ),
  };
}
